import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ball from '../assets/images/ballLunge.jpg';
import kbSquat from '../assets/images/kettlebellSquat.jpg';

function Training() {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <SEO
        title="Personal Training Programs: Strength, Mobility & More"
        description="Discover how strength, muscle mass, mobility, flexibility, balance and cardiovascular training work together to keep you fit, capable and pain-free at any age."
        path="/training"
      />
      <Navbar />

      <main className="flex-grow max-w-7xl mx-auto px-6 lg:px-12 py-6 lg:py-20 text-left">

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-[#162E3B] text-center mb-8">
          Training
        </h1>

        {/* Intro */}
        <div className="space-y-6 mb-12">
          <p className="text-base sm:text-lg text-gray-800 font-semibold leading-relaxed">
          Being fit and healthy starts with physical activity. Humans are supposed to move, not to sit on a couch.
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
          When you're young you naturally have more energy and everything feels easier but as you grow older you'll realize that even simple every day tasks get more and more difficult, you're tired quicker and everything is more demanding on your body.          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
          That is only if you don't train. You can drastically slow down the aging process if you keep yourself physically fit. The major components of physical fitness are strength, muscle mass, mobility, flexibility, balance and cardiovascular health.
          </p>
        </div>

        {/* Grid / Content Blocks */}
        <div className="space-y-12 lg:space-y-16">
          
          {/* Strength & Muscle Mass with Kettlebell Squat image */}
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="lg:w-3/5 space-y-6">
              
              {/* Strength */}
              <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  Strength
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                We use our strength to push, pull, jump, hold onto something physically, pick up a heavy object from the floor, carry something and in many other situations we face in our lives. A lack of strength will definitely make your life harder.
                </p>
                <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl">
                  <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                    💡 Did you know? Grip strength is related to longevity and slowing down the aging process?
                  </p>
                </div>
              </div>

              {/* Muscle Mass */}
              <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  Muscle Mass
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                The first step to gain strength is of course to gain muscle mass. Developing our muscles however will not only make us stronger and look better but will even improve our cardiovascular health. Our muscle will absorb and utilise the oxygen it gets from our blood better which leads to lower blood pressure.                </p>
                <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl">
                  <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                    💡 Did you know? Muscle is more energy consuming than fat and the more muscle mass we have the more calories we burn without doing anything?
                  </p>
                </div>
              </div>

            </div>

            <div className="lg:w-2/5 w-full">
              <img 
                className="w-full h-[400px] lg:h-[500px] object-cover rounded-2xl shadow-lg border border-gray-100" 
                src={kbSquat} 
                alt="Trainer performing kettlebell squats" 
              />
            </div>
          </div>

          {/* Mobility, Flexibility, Balance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Mobility */}
            <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl flex flex-col justify-between">
              <div>
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  Mobility
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                Having a strong body is important but we also need to be able to move it properly. Mobility means the capacity to freely move our joints and our body in general in all possible directions. Our body is required to execute such tasks constantly. A better mobility will give us an easier time doing so and possibly prevent injury.                </p>
              </div>
              <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl mt-4">
                <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                  💡 Did you know? Our body can move in three different planes; the frontal, the lateral and the transverse plane?
                </p>
              </div>
            </div>

            {/* Flexibility */}
            <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl flex flex-col justify-between">
              <div>
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  Flexibility
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                Closely linked to mobility, flexibility basically means the stretchability of your muscles. If you want to be mobile you somewhat need to be flexible as well. Again better flexibility will help us accomplish everyday tasks easier and possibly prevent injury.                </p>
              </div>
              <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl mt-4">
                <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                  💡 Did you know? Most people have tight hamstrings because they sit too much?
                </p>
              </div>
            </div>

          </div>

          {/* Balance & Cardio with Ball Lunge image */}
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="lg:w-2/5 w-full">
              <img 
                className="w-full h-[320px] lg:h-[420px] object-cover rounded-2xl shadow-lg border border-gray-100" 
                src={ball} 
                alt="Trainer performing ball lunges for balance" 
              />
            </div>

            <div className="lg:w-3/5 space-y-6">
              
              {/* Balance */}
              <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  Balance
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                This is probably the one that gets overlooked the most but without balance we wouldn't do very much, not even walk. In fact, you can clearly see this with elderly people. The lack of balance is often a major problem in our overall health as we age.                </p>
                <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl">
                  <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                    💡 Did you know? Falls caused by a lack of balance (and also strength) are one of the leading causes of injury amongst older adults?
                  </p>
                </div>
              </div>

              {/* Cardiovascular Health */}
              <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  Cardiovascular Health
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                Of course we can't forget the most important muscle in our body, the heart. We can deliberately train it by doing a prolonged effort that gets our heart rate up like going for a run or playing sports (football, basketball, tennis, …)                </p>
                <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl">
                  <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                    💡 Did you know? even just going for a little walk once a day reduces the risk of heart disease?
                  </p>
                </div>
              </div>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
          With this being said, we didn't even talk about the visual benefits you'll see looking at yourself in the mirror when you gain muscle and/or lose fat thanks to your training (and nutrition). Working out will obviously get you a more aesthetically pleasing physique and we all know that if you look better you feel better. Physical fitness is the absolute baseline of mental fitness. If you're mentally not in a good place, the first step is to work on the physical aspect of health. Without that, it will be very difficult to work on the mental aspect. In fact, your mental health will naturally improve with your physical health. 
          </p>    
            </div>
          </div>

          {/* Conclusion / Summary block */}
          <div className="bg-[#162E3B] text-white rounded-2xl p-8 sm:p-10 shadow-lg space-y-6">
      
            <h3 className="text-xl sm:text-2xl font-extrabold text-[#FFD82D]">
            "Strong body, strong mind”, is what they say. 
            </h3>
            <p className="text-base sm:text-lg text-gray-200 leading-relaxed">
             And it is certainly true. The confidence boost you get when you realize that you look and feel better will change your daily life by itself.
            </p>
            <p className="text-base sm:text-lg font-bold text-white">
            The sooner you start, the better it is, and it's never too late.
            </p>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Training;
